import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon, { ICON_TYPE } from 'components/icon';
import BaseFooter from 'components/layout/footer';
import { Anchor } from 'components/base';

import { PrivacyPageLink, TermsPageLink } from '../pageLinks';

import SETTINGS from '../../settings';

// Desktop columns, left to right. Phones flatten them into one accordion list in the same order.
// Every product or landing page with an impact on sales should be considered for this list.
const FOOTER_COLUMNS = [
  [
    {
      title: 'Print Sets',
      href: '/print-sets',
      links: [
        ['Square Prints', '/square-prints'],
        ['Classic Prints', '/classic-prints'],
        ['Glossy Photo Prints', '/glossy-prints'],
        ['Colorful Square Prints', '/colorful-square-prints'],
        ['Photo Booth Magnets', '/photobooth-magnets'],
      ],
    },
    {
      title: 'Photo Cards',
      href: '/cards',
      links: [
        ['Custom Holiday Cards', '/custom-photo-holiday-cards'],
        ['Custom Photo Greeting Cards', '/custom-photo-greeting-cards'],
      ],
    },
  ],
  [
    {
      title: 'Wall Art',
      href: '/wall-art',
      links: [
        ['Acrylic Prints', '/acrylic-prints'],
        ['Gallery Wrapped Canvas Prints', '/wrapped-canvas-prints'],
        ['Framed Canvas Prints', '/framed-canvas-prints'],
        ['Metal Wall Prints', '/metal-wall-prints'],
        ['Engineer Prints', '/engineer-prints'],
        ['Square Engineer Prints', '/square-engineer-prints'],
        ['Fine Art Prints', '/artprint'],
        ['Newsprints', '/news-print'],
        ['Photo Wall Ideas', '/photo-wall-ideas'],
      ],
    },
  ],
  [
    {
      title: 'Photo Books',
      href: '/photo-books',
      links: [
        ['Softcover Photo Books', '/softcover-books'],
        ['8x8 Hardcover Photo Book', '/hardcover-book'],
        ['Premium Hardcover Photo Book', '/premium-hardcover-books'],
        ['Baby photo books', '/baby-photo-books'],
        ['How to make a photo book', '/how-to-make-a-photo-book'],
      ],
    },
    {
      title: 'Calendars',
      href: '/calendars',
      links: [
        ['Keepsake Wall Calendar', '/wall-calendar'],
        ['Large Wall Calendar', '/wall-calendar-large'],
        ['Engineer Print Calendars', '/engineer-print-calendar'],
      ],
    },
  ],
  [
    {
      title: 'Desk Art',
      href: '/desk',
      links: [
        ['Metal Desk Prints', '/metal-desk-prints'],
        ['Acrylic Magnets', '/acrylic-magnets'],
        ['Wood Photo Block', '/wood-block'],
      ],
    },
    {
      title: 'Display',
      href: '/display',
      links: [
        ['Wood Poster Rails', '/wooden-rails'],
        ['Photo Rope', '/photo-ropes'],
        ['Square Envelopes', '/square-envelopes'],
      ],
    },
  ],
  [
    {
      title: 'Gifts',
      href: '/gifts',
      links: [
        ['Gifts Under $20', '/gifts-under-20'],
        ['Gifts Under $50', '/gifts-under-50'],
        ['Gift Cards', '/giftcard'],
      ],
    },
    {
      title: 'Ideas & Guides',
      links: [
        ['Your year in photos', '/year-in-photos'],
        ['Photo Print Sizes Guide', '/tools/photo-print-sizes'],
        ['Print Photos from iPhone', '/guides/print-photos-from-iphone'],
      ],
    },
  ],
  [
    {
      title: 'Help',
      links: [
        ['Shipping and Returns', '/shipping'],
        ['FAQ', 'https://knowledge-base--parabo.groovehq.com/help'],
        ['help@parabo.press', 'mailto:help@parabo.press', 'ft-link-accent'],
      ],
    },
    {
      title: 'Company',
      links: [
        ['About Parabo Press', '/about'],
        ['Our Blog', '/blog/'],
        ['Wholesale', '/wholesale'],
        ['Newsletter', '/newsletter'],
        ['Give $10, Get $10', '/refer-friend'],
        ['Get the app', '/app'],
      ],
    },
  ],
];

const SOCIALS = [
  ['X', SETTINGS.X_URL, ICON_TYPE.TWITTER],
  ['Facebook', SETTINGS.FACEBOOK_URL, ICON_TYPE.FACEBOOK],
  ['Instagram', SETTINGS.INSTAGRAM_URL, ICON_TYPE.INSTAGRAM],
  ['Pinterest', SETTINGS.PINTEREST_URL, ICON_TYPE.PINTEREST],
];

const externalProps = (href) =>
  href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {};

const groupId = (title) => `footer-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`;

const Footer = ({ hideOnPhone }) => (
  <BaseFooter className={classNames('ft', { 'ft-hide-on-phone': hideOnPhone })}>
    <div className="ft-inner">
      <nav className="ft-grid" aria-label="Site map">
        {FOOTER_COLUMNS.map((column) => (
          <div key={column[0].title} className="ft-column">
            {column.map((group) => (
              <FooterGroup key={group.title} group={group} />
            ))}
          </div>
        ))}
      </nav>

      <div className="ft-bottom">
        <div className="ft-legal">
          <span className="ft-copy">{SETTINGS.APP_COPY}</span>
          <PrivacyPageLink>
            <Anchor>Privacy Policy</Anchor>
          </PrivacyPageLink>
          <TermsPageLink>
            <Anchor>Terms of Use</Anchor>
          </TermsPageLink>
        </div>

        <div className="ft-brand">
          <ul className="ft-socials">
            {SOCIALS.map(([label, href, type]) => (
              <li key={label}>
                <a href={href} className="ft-social" aria-label={label} {...externalProps(href)}>
                  <Icon type={type} size={18} />
                </a>
              </li>
            ))}
          </ul>
          <span className="ft-divider" aria-hidden="true" />
          <a
            href={SETTINGS.APP_STORE_URL}
            className="link-app-store"
            {...externalProps(SETTINGS.APP_STORE_URL)}
          >
            <img
              src="/static/img/app-store-badge.svg"
              alt="Download Parabo Press on the App Store"
              width="120"
              height="40"
              loading="lazy"
            />
          </a>
        </div>
      </div>
    </div>
  </BaseFooter>
);

// Links always render into the HTML so crawlers see the full site map. Below md the list is
// hidden by CSS until the toggle opens it, from md up the list is always visible.
const FooterGroup = ({ group }) => {
  const [open, setOpen] = useState(false);
  const listId = groupId(group.title);

  return (
    <div className={open ? 'ft-group is-open' : 'ft-group'}>
      <h2 className="ft-title">
        {group.href ? (
          <a href={group.href} className="ft-title-text">
            {group.title}
          </a>
        ) : (
          <span className="ft-title-text">{group.title}</span>
        )}
        <button
          type="button"
          className="ft-toggle"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen(!open)}
        >
          {group.title}
          <svg className="ft-chevron" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              d="m6 9 6 6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </h2>
      <ul id={listId} className="ft-links">
        {/* Phones hide the title link, so the hub stays reachable from inside the group */}
        {group.href && (
          <li className="ft-hub">
            <a href={group.href}>All {group.title}</a>
          </li>
        )}
        {group.links.map(([label, href, className]) => (
          <li key={href}>
            <a href={href} className={className} {...externalProps(href)}>
              {label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

Footer.propTypes = {
  hideOnPhone: PropTypes.bool,
};

FooterGroup.propTypes = {
  group: PropTypes.shape({
    title: PropTypes.string.isRequired,
    href: PropTypes.string,
    links: PropTypes.arrayOf(PropTypes.array).isRequired,
  }).isRequired,
};

export default Footer;
